import type { CountryCode } from "libphonenumber-js"

import * as isoCountries from "i18n-iso-countries"
import enCountries from "i18n-iso-countries/langs/en.json"
import esCountries from "i18n-iso-countries/langs/es.json"
import frCountries from "i18n-iso-countries/langs/fr.json"
import itCountries from "i18n-iso-countries/langs/it.json"
import { getCountries, getCountryCallingCode } from "libphonenumber-js"

import { getLocales } from "expo-localization"

isoCountries.registerLocale(enCountries)
isoCountries.registerLocale(esCountries)
isoCountries.registerLocale(frCountries)
isoCountries.registerLocale(itCountries)

const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - "A".charCodeAt(0)

/**
 * Convert an ISO 3166-1 alpha-2 country code to its flag emoji
 * using regional indicator symbols (no library needed).
 */
export function countryCodeToFlagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .replace(/[A-Z]/g, (char) =>
      String.fromCodePoint(char.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET),
    )
}

export interface PhoneCountrySelectOption {
  label: string
  value: CountryCode
}

const optionsCache = new Map<string, PhoneCountrySelectOption[]>()

function getCountryDisplayName(
  countryCode: CountryCode,
  languageCode: string,
): string {
  // Non-ISO codes supported by libphonenumber (e.g. AC, TA) have no
  // translated name; fall back to the raw code.
  return isoCountries.getName(countryCode, languageCode) ?? countryCode
}

/**
 * Build the option list for the phone country selector, localized to the
 * given app language and sorted by country name.
 */
export function buildPhoneCountryOptions(
  languageCode: string,
): PhoneCountrySelectOption[] {
  const language = isoCountries.langs().includes(languageCode)
    ? languageCode
    : "en"
  const cached = optionsCache.get(language)
  if (cached) {
    return cached
  }
  const options = getCountries()
    .map((countryCode) => {
      const name = getCountryDisplayName(countryCode, language)
      return {
        label: `${countryCodeToFlagEmoji(countryCode)} ${name} (+${getCountryCallingCode(countryCode)})`,
        name,
        value: countryCode,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name, language))
    .map(({ label, value }) => ({ label, value }))
  optionsCache.set(language, options)
  return options
}

/**
 * Compact display for the closed select, e.g. "🇺🇸 +1".
 */
export function getPhoneCountryValueText(countryCode: CountryCode): string {
  return `${countryCodeToFlagEmoji(countryCode)} +${getCountryCallingCode(countryCode)}`
}

/**
 * Default phone country from the device locale, falling back to US.
 */
export function getDefaultPhoneCountry(): CountryCode {
  const regionCode = getLocales()[0]?.regionCode
  if (
    typeof regionCode === "string" &&
    (getCountries() as string[]).includes(regionCode.toUpperCase())
  ) {
    return regionCode.toUpperCase() as CountryCode
  }
  return "US"
}
